import { useCallback, useEffect } from 'react';
import { currentLanguage } from '../currentLanguage';
import { BlockerFunction, useBlocker } from 'react-router-dom';
import { ConfirmDialogProps } from './ConfirmDialog/ConfirmDialog';
import { connect } from '../rootReducer';
import { showConfirmDialog } from '../appActions';
import { RouteEnum, TeamViewMode, getRouteState } from '../routes';
import {
  isCreatedShift,
  isNewlyCreatedShift,
  ShiftDraft,
} from '../ListView/CreateShift/EditedShift';
import { setShiftDraft } from '../ShiftView/ShiftViewActions';
import { ModalVariant } from '@pdcfrontendui/components';

/**
 * A router may only have one blocker at a time.
 * Therefore this component works as a single blocker for all confirmations.
 */
export const RouteBlocker = connect<
  { shiftDraft: ShiftDraft | null },
  {
    showDialog: (modal: Omit<ConfirmDialogProps, 'shown'>) => void;
    removeShiftDraft: () => void;
  }
>(
  (store) => ({ shiftDraft: store.shiftViewReducer.shiftDraft }),
  (dispatch) => ({
    showDialog: (modal) => {
      dispatch(showConfirmDialog(modal));
    },
    removeShiftDraft: () => {
      dispatch(setShiftDraft(null));
    },
  })
)(({ showDialog, shiftDraft, removeShiftDraft }) => {
  const shouldBlock: BlockerFunction = useCallback(
    ({ currentLocation, nextLocation }) => {
      if (!shiftDraft) {
        return false;
      }
      const curr = getRouteState(currentLocation);
      const next = getRouteState(nextLocation);
      if (curr?.route === RouteEnum.Team) {
        // Logging out already has a confirmation dialog
        if (next?.route === RouteEnum.Login) {
          return false;
        }
        // Prevent redirecting while create shift is open (to other views than find substitute)
        if (
          curr.params.mode === TeamViewMode.CreateOrEdit &&
          (next?.route !== RouteEnum.Team ||
            next.params.mode !== TeamViewMode.FindSubstitute)
        ) {
          return true;
        }
        if (
          curr.params.mode === TeamViewMode.FindSubstitute &&
          (next?.route !== RouteEnum.Team ||
            (next.params.mode !== TeamViewMode.FindSubstitute &&
              next.params.mode !== TeamViewMode.CreateOrEdit))
        ) {
          return true;
        }
      }
      return false;
    },
    [shiftDraft]
  );

  const blocker = useBlocker(shouldBlock);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      if (shiftDraft) {
        showDialog({
          title: currentLanguage.ConfirmLeavePage,
          message:
            isNewlyCreatedShift(shiftDraft) || isCreatedShift(shiftDraft)
              ? currentLanguage.ConfirmLeavePageContentCreated
              : currentLanguage.ConfirmLeavePageContentEdited,
          buttonCancelText: currentLanguage.Cancel,
          buttonOkText: currentLanguage.YesLeavePage,
          onCancel: () => blocker.reset(),
          onOk: removeShiftDraft,
          variant: ModalVariant.Alert,
        });
      } else {
        blocker.proceed();
      }
    }
  }, [blocker, showDialog, removeShiftDraft, shiftDraft]);

  return null;
});
